/**
 * Restaurant conversion — consumer write/read path.
 *
 * A "conversion" is a `visits` row: the diner engaged with a venue in-app and
 * then physically went there. Two capture paths create one (`method`):
 *   - "check_in" — the diner tapped "Dine In!" while at the venue,
 *   - "geo"      — a foreground 10-min low-movement dwell inside a tight radius.
 *
 * The browser has NO insert/update grant on `visits` (schema §21); every write
 * goes through the security-definer RPCs added in §23 (record_visit, hardened in
 * §31 / submit_feedback / my_visits). We pass `getAnonId()` so guests who consented
 * are attributed too — the RPC resolves `user_id` from the session server-side.
 *
 * Everything here is fail-soft: a network/permission hiccup returns null / [] and
 * never throws into the app (mirrors analytics.ts).
 */
import { supabase } from "@/lib/supabase";
import { getAnonId } from "@/lib/anonId";
import type { LatLng } from "@/lib/geo";
import type { RestaurantId } from "@/types/restaurant";

/** How a conversion was captured (subset of the §21 vocab the client can write). */
export type VisitMethod = "check_in" | "geo";

/** One of the caller's own conversions, as returned by `my_visits`. */
export interface MyVisit {
  id: number;
  venue_id: RestaurantId;
  venue_name: string;
  city: string | null;
  method: string;
  detected_at: string;
  rating: number | null;
  spend_ron: number | null;
  feedback: string | null;
  feedback_at: string | null;
}

/**
 * Record a conversion for the current diner. Returns the visit row (a fresh one,
 * or the existing same-day row — the RPC dedups), or null if it couldn't be
 * written (e.g. a guest who never consented has no identity to attribute, or the
 * server's 75 m check refused the position).
 *
 * `coords` is the diner's current fix. Schema §31c re-checks the distance to the
 * venue on the server and discards the coordinates; they are never stored.
 */
export async function recordVisit(
  venueId: RestaurantId,
  method: VisitMethod,
  coords: LatLng,
  accuracyM?: number | null,
): Promise<MyVisit | null> {
  try {
    const anon = getAnonId();
    const { data, error } = await supabase.rpc("record_visit", {
      p_venue: venueId,
      p_method: method,
      p_lat: coords.lat,
      p_lng: coords.lng,
      p_anon: anon,
      p_accuracy: accuracyM ?? null,
    });
    if (error || !data) return null;
    // record_visit returns a `visits` row; normalise to the MyVisit shape used by the UI.
    const row = data as Record<string, unknown>;
    return {
      id: Number(row.id),
      venue_id: String(row.venue_id),
      venue_name: "",
      city: (row.city as string) ?? null,
      method: String(row.method),
      detected_at: String(row.detected_at),
      rating: (row.rating as number) ?? null,
      spend_ron: (row.spend_ron as number) ?? null,
      feedback: (row.feedback as string) ?? null,
      feedback_at: (row.feedback_at as string) ?? null,
    };
  } catch {
    return null;
  }
}

/** Attach feedback to a conversion the caller owns. Rating (1..5) is required. */
export async function submitFeedback(input: {
  visitId: number;
  rating: number;
  spend?: number | null;
  feedback?: string | null;
}): Promise<boolean> {
  try {
    const { error } = await supabase.rpc("submit_feedback", {
      p_visit_id: input.visitId,
      p_rating: input.rating,
      p_spend: input.spend ?? null,
      p_feedback: input.feedback ?? null,
      p_anon: getAnonId(),
    });
    return !error;
  } catch {
    return false;
  }
}

/** The caller's own conversions, newest first — for the Feedbacks tab + prompt. */
export async function listMyVisits(): Promise<MyVisit[]> {
  try {
    const { data, error } = await supabase.rpc("my_visits", {
      p_anon: getAnonId(),
      p_limit: 50,
    });
    if (error || !Array.isArray(data)) return [];
    return data as MyVisit[];
  } catch {
    return [];
  }
}
